import React, { ReactElement, useState } from 'react';
import { Text, View, Interactable, Image, NavigationIcon } from '@24i/nxg-sdk-quarks';
import { useTheme } from '@24i/nxg-sdk-higgs';
import { overridable } from '@24i/nxg-sdk-gluons/src/context/ComponentOverrides';
import { ViewStyle } from 'react-native';
import getMenuItemStyles, { getTopBarMenuItemStyles } from '../styles';
import { MenuItemPropsWeb, MenuItemStylesWeb } from '../types';

const MenuItem = React.forwardRef((props: MenuItemPropsWeb, ref: typeof MenuItem): ReactElement => {
    const {
        title = '',
        icon = null,
        onPress = () => null,
        styles: getStyles = getMenuItemStyles,
        isHovered: isHoveredFromProps,
        active = false,
        testID = null,
        additionalTextStyles,
        additionalItemStyles,
        hasDividerUnder,
        breakText = false,
        enlargeIcon = false,
        variant,
    } = props;

    const [isItemHovered, setItemIsHovered] = useState(false);
    const isHovered = typeof isHoveredFromProps === 'boolean' ? isHoveredFromProps : isItemHovered;
    const { theme } = useTheme();
    const styles = getStyles(theme, variant) as MenuItemStylesWeb;

    const getTestId = () => {
        const itemTitle = title || icon;

        return itemTitle
            ? `${itemTitle.trim().toLowerCase().replace(/\s/g, '_')}_button`
            : undefined;
    };

    const isUrlIcon = Boolean(typeof icon === 'string' && icon.match(/.(jpg|jpeg|png|gif|svg)$/i));

    const renderContent = (): ReactElement => (
        <View style={styles.itemContainer} testID={`topmenu-item-${title}`}>
            {icon != null && !isUrlIcon && (
                <NavigationIcon
                    iconName={icon}
                    size={styles.icon?.fontSize || 25}
                    iconStyles={[
                        styles.iconImage,
                        ...(active || isHovered ? ([styles.iconActive] as ViewStyle[]) : []),
                        ...(enlargeIcon ? [styles.enlargeIcon] : []),
                    ]}
                    isActive={active}
                />
            )}
            {icon != null && isUrlIcon && (
                <View style={styles.profileAvatarImageContainer}>
                    <Image
                        source={{
                            uri: icon,
                        }}
                    />
                </View>
            )}
            {/* FIXME: double-check and then delete below */}
            {/* {menuItemIcon &&
                (isIconMapImage(menuItemIcon) ? (
                    <Image
                        style={[
                            styles.iconImage,
                            ...(active || isHovered ? ([styles.iconActive] as ViewStyle[]) : []),
                            ...(enlargeIcon ? [styles.enlargeIcon] : []),
                        ]}
                        source={{
                            uri: getImageUrl(
                                {
                                    ...defaultSize,
                                    imagePath:
                                        active && menuItemIcon.activeUri
                                            ? menuItemIcon.activeUri
                                            : menuItemIcon.uri,
                                },
                                [
                                    {
                                        url:
                                            active && menuItemIcon.activeUri
                                                ? menuItemIcon.activeUri
                                                : menuItemIcon.uri,
                                        size: defaultSize,
                                    },
                                ]
                            ),
                        }}
                        fallbackImage={() => (
                            <LegacyIcon
                                size={styles.icon?.fontSize || 25}
                                font="materialIcons"
                                name="image"
                                color={styles.icon?.color}
                            />
                        )}
                        hasFallbackGradient={false}
                    />
                ) : (
                    <View style={icon === 'plus' && styles.plusBackground}>
                        <LegacyIcon
                            style={[styles.icon, (active || isHovered) && styles.iconActive]}
                            color={styles.icon?.color}
                            size={styles.icon?.fontSize || 25}
                            {...menuItemIcon}
                        />
                    </View>
                ))}
            {!menuItemIcon && icon && isValidImageUrl(icon) && (
                <View style={styles.profileAvatarImageContainer}>
                    <Image
                        source={{
                            uri: icon,
                        }}
                    />
                </View>
            )} */}
            {!title ? null : (
                <View style={styles.titleContainer}>
                    <Text
                        numberOfLines={breakText ? 0 : 1}
                        style={[
                            styles.title,
                            (active || isHovered) && styles.titleActive,
                            additionalTextStyles,
                        ]}
                    >
                        {title}
                    </Text>
                </View>
            )}
            {hasDividerUnder && <View style={styles.lineDivider} />}
        </View>
    );

    return (
        <Interactable
            ref={ref}
            style={[styles.holder, active && styles.holderActive, additionalItemStyles]}
            onMouseEnter={() => setItemIsHovered(true)}
            onMouseLeave={() => setItemIsHovered(false)}
            onPress={onPress}
            testID={testID || getTestId()}
        >
            {renderContent()}
        </Interactable>
    );
});

MenuItem.displayName = 'MenuItem';

const TopBarMenuItem = (props: MenuItemPropsWeb): ReactElement => (
    <MenuItem styles={getTopBarMenuItemStyles} {...props} />
);
const OverridableTopBarMenu = overridable(TopBarMenuItem, 'TopBarMenuItem');
export { OverridableTopBarMenu as TopBarMenuItem, getTopBarMenuItemStyles, getMenuItemStyles };

export default overridable(MenuItem, 'MenuItem');
